<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import store from "@/store";

import { maskCurrency } from "@/common/utils/mask";
import { toast } from "@/common/utils/toast";

import { termList, termCreate, termDelete } from "@/common/api/rest/term/term";

import type { TermType, TermPayloadType } from "@/common/types/term/term";

import {
	breadcrumbSimple,
	baseConfirm,
	baseModal,
	baseTable,
	svgIcon,
} from "@/common/components";

import FormTerm from "@/components/forms/form-term.vue";

const { t, tm } = useI18n();
const router = useRouter();

const tableData = ref<TermType[]>([]);
const payload = ref<TermPayloadType | null>(null);

const selected = ref<TermType | null>(null);

const limit = ref<number>(6);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);
const loading = ref(false);

const tableHeader = computed(() => [
	{ key: "name", label: t("common.labels.name") },
	{ key: "code", label: t("common.labels.code") },
	{ key: "status", label: t("common.labels.status") },
]);

const breadcrumbList = computed(() => [
	{
		key: "register",
		label: t("common.labels.registrations"),
		url: "/register",
	},
	{
		key: "terms",
		label: t("common.labels.terms"),
		url: "/register/term",
	},
]);

const statusOptions = computed(() => ({
	ACTIVE: t("common.labels.active"),
	INACTIVE: t("common.labels.inactive"),
}));

function fetchData() {
	loading.value = true;

	termList({
		page: page.value,
		limit: limit.value,
		search: search.value,
	})
		.then((response) => {
			const { success, data, message } = response;
			if (success) {
				tableData.value = data.list?.map((item: TermType) => {
					return {
						...item,
					};
				});
				pages.value = data.infos.pages;
				total.value = data.infos.total;
				searched.value = data.infos.count;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			loading.value = false;
		});
}

function changePage(value: number) {
	page.value = value;
	fetchData();
}

function searchItem(value: string) {
	search.value = value;

	fetchData();
}

function addItem() {
	showModalAdd.value = true;
}

function addItemCancel() {
	showModalAdd.value = false;
	payload.value = null;
}

function addItemConfirm() {
	if (!payload.value) return;

	store.commit("SET_LOADING", true);

	termCreate(payload.value)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				editItem(data);
				toast({ message: message, type: "success" });
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function editItem(data: TermType) {
	router.push(`/register/term/${data.term_id}/edit`);
}

function removeItem(data: TermType) {
	selected.value = data;
	showModalDel.value = true;
}

function removeItemCancel() {
	showModalDel.value = false;
	selected.value = null;
}

function removeItemConfirm() {
	if (!selected.value) return;

	termDelete(selected.value.term_id)
		.then((response) => {
			const { success, message } = response;

			fetchData();
			removeItemCancel();
			toast({ message: message, type: success ? "success" : "error" });
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function formatCurrency(value: number, currency: any) {
	const price = Number.parseFloat(value?.toString()).toFixed(2);
	return maskCurrency(price, currency);
}

onMounted(() => {
	fetchData();
});
</script>

<template>
	<article class="register-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="terms"
			></breadcrumb-simple>
		</section>

		<section class="page-content">
			<div class="tab-header">
				<svg-icon icon="no-image" size="2rem" />
				{{ t("common.labels.terms") }}
			</div>

			<div class="tab-content">
				<base-table
					:header="tableHeader"
					:data="tableData"
					:limit="limit"
					:page="page"
					:pages="pages"
					:total="total"
					:searched="searched"
					:loading="loading"
					:options="true"
					:del="true"
					:edit="true"
					@change:page="changePage($event)"
					@searching="searchItem($event)"
					@btn:add="addItem()"
					@btn:edit="editItem($event)"
					@btn:del="removeItem($event)"
				>
					<template #status="{ data }">
						{{ statusOptions[data.status] }}
					</template>
				</base-table>
			</div>
		</section>
	</article>

	<base-modal
		:button-confirm="t('common.labels.save')"
		:button-close="t('common.labels.cancel')"
		:title="t('term.modals.titles.term_add').toUpperCase()"
		:show="showModalAdd"
		:show-button-confirm="true"
		@close="addItemCancel()"
		@confirm="addItemConfirm()"
	>
		<template #content>
			<form-term
				:data="selected"
				@update:data="payload = $event"
				@validate="payloadValid = $event"
			/>
		</template>
	</base-modal>

	<base-confirm
		:show="showModalDel"
		:title="t('term.modals.titles.term_remove').toUpperCase()"
		:message="
			t('term.modals.messages.term_remove', {
				name: selected?.name?.toUpperCase(),
			})
		"
		@cancel="removeItemCancel()"
		@confirm="removeItemConfirm()"
	/>
</template>

<style lang="scss" scoped>
.register-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		background: white;
		border-radius: 0.5rem;

		.tab-header {
			display: inline-flex;
			gap: 0.5rem;
			font-size: 1.2rem;
			align-items: center;
			padding-inline: 1.25rem;
			padding-block: 0.75rem;
			border-block-end: 1px solid var(--border);
			text-transform: uppercase;
		}

		.tab-content {
			display: flex;
			flex-direction: column;
			padding-inline: 1.5rem;
			padding-block: 1rem;
			gap: 1rem;
		}
	}
}
</style>
